import { unregisteredFieldLabel, unregisteredScopeEntries } from '@/pages/recognition/constants';
import { toNumber } from '@/utils/common';

// Draft modes: 'unset' (division === null, create forms), 'everyone', 'filters' (one row per field).
// Rows keep keys and values that no longer exist, so saving never widens an audience silently.

const fieldFor = (key, scopeFields) => (scopeFields || []).find(f => f.key === key);

export const fieldLabel = (key, scopeFields) => fieldFor(key, scopeFields)?.label || unregisteredFieldLabel(key);

export const valueLabel = (key, value, scopeFields) =>
  fieldFor(key, scopeFields)?.values?.find(v => String(v.value) === String(value))?.label || String(value);

export const operatorLabel = count => (count > 1 ? 'is any of' : 'is');

export function staleEntries(scope, scopeFields) {
  const missing = unregisteredScopeEntries(scope, scopeFields).map(entry => ({
    key: entry.key,
    label: unregisteredFieldLabel(entry.key),
    values: entry.values,
    fieldMissing: true,
  }));
  const goneValues = (scopeFields || []).flatMap(field => {
    const saved = (scope?.[field.key] || []).map(String);
    const known = new Set((field.values || []).map(v => String(v.value)));
    const gone = saved.filter(v => !known.has(v));
    return gone.length ? [{ key: field.key, label: field.label, values: gone, fieldMissing: false }] : [];
  });
  return [...missing, ...goneValues];
}

export function draftFromScope(scope, scopeFields) {
  if (!scope || scope.division === null) {
    return { mode: 'unset', rows: [] };
  }
  const staleKeys = new Set(staleEntries(scope, scopeFields).map(e => e.key));
  const fieldOrder = (scopeFields || []).map(f => f.key);
  const rank = key => (staleKeys.has(key) ? -1 : fieldOrder.indexOf(key));
  const rows = Object.entries(scope)
    .filter(([, values]) => values && values.length)
    .map(([key, values]) => ({ key, values: values.map(String) }))
    .sort((a, b) => rank(a.key) - rank(b.key));
  return rows.length ? { mode: 'filters', rows } : { mode: 'everyone', rows: [] };
}

export function scopeFromDraft(draft, scopeFields) {
  const scope = Object.fromEntries((scopeFields || []).map(f => [f.key, []]));
  if (draft.mode === 'unset') {
    return { ...scope, division: null };
  }
  if (draft.mode === 'filters') {
    for (const row of draft.rows) {
      if (row.values.length) {
        scope[row.key] = row.values.map(String);
      }
    }
  }
  return scope;
}

// The one place the audience size is shown; null hides the line (the editor explains zero or a failed count).
export function audienceCountText({ mode, count = null, total = null, loading = false }) {
  if (mode === 'unset') {
    return null;
  }
  if (loading) {
    return 'Counting employees…';
  }
  const n = mode === 'everyone' ? total : count;
  if (!n) {
    return null;
  }
  return n === 1 ? '1 employee can receive this award' : `${toNumber(n)} employees can receive this award`;
}

// Nominators outside Who can receive are left out of the list, but turn up greyed out when
// searched for, so admins can see why they can't be chosen. eligibleIds is null when the
// award goes to everyone.
export const markOutOfScope = (options, eligibleIds) =>
  eligibleIds
    ? options.map(o =>
        eligibleIds.has(o.value)
          ? o
          : { ...o, text: `${o.text} · Can't receive this award`, disabled: true, searchOnly: true },
      )
    : options;

// Nominators must be able to receive the award; changing who can receive drops the rest.
export const keepEligible = (ids, allowed) => {
  const kept = (ids || []).filter(id => allowed.has(id));
  return { kept, removed: (ids || []).length - kept.length };
};

export const removedNominatorsText = n =>
  `Removed ${n} ${n === 1 ? 'nominator' : 'nominators'} who can no longer receive this award.`;

export const sameIds = (a, b) => {
  const left = new Set(a || []);
  const right = new Set(b || []);
  return left.size === right.size && [...left].every(id => right.has(id));
};
